// local storage keys
import LSKEYS from './LSKEYS'

/**
 * AuthDriver
 * implements AuthDriver interface
 * @param opts options object requires:
 *  - SCOPE
 *  - CLIENT_ID
 *  - REDIRECT_URI - URI to redirect to after a login/logout request
 *  - SLUG - auth.{SLUG}.redbearapp.io/...
 *  - PROVIDER - Google/AAD can force which provider to use to login
 * @constructor
 */
function AuthDriver(opts) {
  this.access_token = null
  this.xsrf_token = null
  this.authContext = null
  // required options
  if (!opts.SCOPE) {
    throw new Error('SCOPE is required in AuthDriver')
  }
  this.SCOPE = opts.SCOPE
  if (!opts.CLIENT_ID) {
    throw new Error('CLIENT_ID is required in AuthDriver')
  }
  this.CLIENT_ID = opts.CLIENT_ID
  if (!opts.REDIRECT_URI) {
    throw new Error('REDIRECT_URI is required in AuthDriver')
  }
  this.REDIRECT_URI = opts.REDIRECT_URI
  if (!opts.SLUG) {
    throw new Error('SLUG is required in AuthDriver')
  }
  this.SLUG = opts.SLUG
  this.PROVIDER = opts.PROVIDER
  Object.defineProperties(this, {
    accessToken: {
      get: () => this.access_token,
      set: token => {
        // logged in
        window.localStorage.setItem(LSKEYS.ACCESS_TOKEN, token)
        this.access_token = token
        window.localStorage.setItem(LSKEYS.DECODED_TOKEN, JSON.stringify(this.decodedToken.payload))
        // if reactiveChange is defined in authContext then update the reactive interface
        if (this.authContext.reactiveChange) {
          this.authContext.reactiveChange('accessToken', token)
          this.authContext.reactiveChange('decodedToken', this.decodedToken)
        }
      }
    },
    decodedToken: {
      get: () => {
        let splitToken = this.accessToken.split('.')
        return {
          header: decode(splitToken[0]),
          payload: decode(splitToken[1])
        }
      }
    },
    xsrfToken: {
      get: () => this.xsrf_token,
      set: token => {
        window.localStorage.setItem(LSKEYS.XSRF_TOKEN, token)
        this.xsrf_token = token
        if (this.authContext.reactiveChange) {
          this.authContext.reactiveChange('xsrfToken', token)
        }
      }
    }
  })
}

/**
 * handles returning redirect by decoding the hash that Api returns
 * also handles loading access_token stored in localStorage
 * then initializes http instance
 * @param authContext Authentication handler object
 */
AuthDriver.prototype.init = function (authContext) {
  // set the authContext
  this.authContext = authContext
  // is the application coming from a redirect?
  let returned = readHash(window.location.hash)
  if (returned) {
    // silent login error, try logging in again with silent off
    if (returned.error_description) { //&& returned.error_description.includes('AADSTS50058')) {
      this.login(false)
    }
    // hash contains the access_token
    if (returned.access_token) {
      this.accessToken = returned.access_token
    }
    if (returned.xsrf_token) {
      this.xsrfToken = returned.xsrf_token
    }
  } else {
    // not coming from redirect, so check if id token is stored in localStorage
    let accessToken = window.localStorage.getItem(LSKEYS.ACCESS_TOKEN)
    if (accessToken) {
      // id token was stored, so we are logged in
      this.accessToken = accessToken
    }
    let xsrfToken = window.localStorage.getItem(LSKEYS.XSRF_TOKEN)
    if (xsrfToken) {
      this.xsrfToken = xsrfToken
    }
  }
  authContext.configureHttp()
  //removes /#access_token=XXXX&token_type=XXXX&xsrf_token=XXXX&state= from the URL
  history.pushState(null, document.title, window.location.pathname + window.location.search)
}

AuthDriver.prototype.login = function (silent) {
  // redirect to login
  window.location.replace(this.makeLoginUri(silent))
}

/**
 * Clear localStorage and redirect to microsoft logout endpoint
 */
AuthDriver.prototype.logout = function () {
  window.localStorage.removeItem(LSKEYS.ACCESS_TOKEN)
  window.localStorage.removeItem(LSKEYS.XSRF_TOKEN)
  window.localStorage.removeItem(LSKEYS.AUTH_STYLE)
  window.location.replace(`https://auth.${this.SLUG}.redbearapp.io/connect/logout?redirect_uri=${this.REDIRECT_URI}`)
}
/**
 * generates and saves a nonce to be compared on successful redirect, then returns a uri to login to Api
 * @param silent whether to add a no prompt option to the uri
 * @return {string} uri to request id token from Api
 */
AuthDriver.prototype.makeLoginUri = function (silent) {
  // generate the base uri
 let uri = `https://auth.${this.SLUG}.redbearapp.io/connect/token` +
    `?client_id=${this.CLIENT_ID}` +
    '&response_type=token' +
    `&redirect_uri=${this.REDIRECT_URI}` +
    `&scope=${this.SCOPE}` +
    `&grant_type=implicit` 
  if (this.PROVIDER) {
      uri += `&providerId=${this.PROVIDER}`
  }
  // if silent then uri will not prompt, but will error if not logged in
  if (silent) {
    uri += '&silent=true'
  }
  return uri
}

/*
 * uses an ajax post to send a silent login request to Api for a new access_token
 */
AuthDriver.prototype.backgroundLogin = async function () {

  let driver = this;
  let response = await $.ajax({
    type: "POST",
    data: `client_id=${this.CLIENT_ID}` +
      '&response_type=token' +
      `&redirect_uri=${this.REDIRECT_URI}` +
      `&scope=${this.SCOPE}` +
      `&grant_type=token`,
    url: `https://auth.${this.SLUG}.redbearapp.io/connect/token`,
    xhrFields: {
      withCredentials: true
    },
    crossDomain: true,
    success: function (response) {
      driver.accessToken = response.access_token
      driver.xsrfToken = response.xsrf_token
    },
    error: function () {
      driver.login()
    }

  });
  return response

}

/**
 * Converts a hash response to an object
 * @param hash
 * @return hash object or false
 */
function readHash(hash) {
  let hsh = hash.slice(1)
  if (hsh) {
    let list = hsh.split('&')
    let obj = {}
    for (let kv of list) {
      let [key, val] = kv.split('=')
      obj[key] = decodeURIComponent(val)
    }
    return obj
  }
  return false
}

/**
 * Decodes a url safe base64 string to object
 * @param b64
 * @return {any}
 */
function decode(b64) {
  try {
    return JSON.parse(window.atob(b64.replace(/-/g, '+').replace(/_/g, '/')))
  } catch (e) {
    if (e.name === 'SyntaxError') {
      return {
        error: 'Malformed token'
      }
    } else {
      throw e
    }
  }
}
// str byteToHex(uint8 byte)
//   converts a single byte to a hex string
// function byteToHex(byte) {
//   return ('0' + byte.toString(16)).slice(-2)
// }

export {
  AuthDriver,
  AuthDriver as
  default
}
